<script setup lang="ts">
import BaseImage from '@/components/ui/BaseImage.vue'
import { computed } from 'vue'
import { formatDate } from '@/utils/stringFormatting'
import type { Conversation, ImageFocus } from '@/types/domain'

type HeroConversation = Conversation & {
  character_name?: string | null
  character_img?: string | null
  character_img_focus?: ImageFocus | null
}

const props = withDefaults(defineProps<{
  conversation?: HeroConversation | null
  loading?: boolean
}>(), {
  conversation: null,
  loading: false,
})

const emit = defineEmits<{
  continue: [conversation: HeroConversation]
  'start-new': []
}>()

const title = computed(() => {
  if (!props.conversation) return ''
  return props.conversation.character_name || 'Continue your story'
})

const preview = computed(() => {
  if (!props.conversation) return ''
  return props.conversation.last_message_preview || ''
})

const timestamp = computed(() => {
  if (!props.conversation) return ''
  const date = props.conversation.last_message_at || props.conversation.created_at
  return date ? formatDate(date) : ''
})
</script>

<template>
  <!-- Loading skeleton -->
  <div v-if="props.loading" class="hero-card hero-card--skeleton" aria-hidden="true">
    <div class="hero-card__portrait-area hero-card__portrait-area--skeleton" />
    <div class="hero-card__content">
      <div class="hero-card__skeleton-line hero-card__skeleton-line--title" />
      <div class="hero-card__skeleton-line" />
      <div class="hero-card__skeleton-line hero-card__skeleton-line--short" />
    </div>
  </div>

  <!-- Has a conversation: hero layout -->
  <div v-else-if="props.conversation" class="hero-card" role="article">
    <div class="hero-card__portrait-area">
      <BaseImage
        class="hero-card__portrait"
        :src="props.conversation.character_img"
        :alt="props.conversation.character_name || 'Character'"
        :image-focus="props.conversation.character_img_focus"
      />
      <div class="hero-card__portrait-fade" aria-hidden="true" />
    </div>

    <div class="hero-card__content">
      <p class="hero-card__eyebrow">Continue with</p>
      <h2 class="hero-card__title">{{ title }}</h2>
      <p v-if="preview" class="hero-card__preview">{{ preview }}</p>
      <p v-if="timestamp || props.conversation.message_count != null" class="hero-card__timestamp">
        {{ timestamp }}<template v-if="timestamp && props.conversation.message_count != null"> · </template><template v-if="props.conversation.message_count != null">{{ props.conversation.message_count }} messages</template>
      </p>

      <button
        type="button"
        class="hero-card__cta"
        @click="emit('continue', props.conversation)"
      >
        Continue →
      </button>
    </div>
  </div>

  <!-- No conversations: empty state -->
  <div v-else class="hero-card hero-card--empty">
    <div class="hero-card__empty-icon" aria-hidden="true">✦</div>
    <h2 class="hero-card__empty-title">Your story starts here</h2>
    <p class="hero-card__empty-message"></p>
    <button
      type="button"
      class="hero-card__cta"
      @click="emit('start-new')"
    >
      Browse characters →
    </button>
  </div>
</template>

<style scoped>
.hero-card {
  position: relative;
  display: flex;
  align-items: stretch;
  border-radius: 16px;
  overflow: hidden;
  background: var(--theme-surface);
  border: 1px solid var(--theme-border);
  min-height: 200px;
}

/* Portrait area */
.hero-card__portrait-area {
  position: relative;
  width: 260px;
  flex-shrink: 0;
  overflow: hidden;
}

.hero-card__portrait {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Right-edge gradient fade that blends portrait into the dark content area */
.hero-card__portrait-fade {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, transparent 40%, var(--theme-surface-solid) 100%);
}

/* Content */
.hero-card__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 24px 28px 24px 20px;
}

.hero-card__eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--theme-text-tertiary);
  margin: 0;
}

.hero-card__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--theme-on-surface);
  margin: 0;
  line-height: 1.25;
  /* limit to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hero-card__preview {
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--theme-text-secondary);
  font-style: italic;
  margin: 0;
  /* limit to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hero-card__timestamp {
  font-size: 0.75rem;
  color: var(--theme-text-faint);
  margin: 0;
}

.hero-card__cta {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 9px 20px;
  background: var(--theme-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  transition: opacity 150ms, transform 150ms;
}

.hero-card__cta:hover { opacity: 0.9; }
.hero-card__cta:active { transform: scale(0.98); }

/* Empty state variant */
.hero-card--empty {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 32px;
  gap: 12px;
  background: var(--theme-primary-subtle);
  border: 1px dashed var(--theme-primary-border);
}

.hero-card__empty-icon {
  font-size: 2.5rem;
  color: var(--theme-primary);
  line-height: 1;
}

.hero-card__empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--theme-on-surface);
  margin: 0;
}

.hero-card__empty-message {
  font-size: 0.9rem;
  color: var(--theme-text-tertiary);
  margin: 0;
  max-width: 320px;
}

/* Skeleton */
.hero-card--skeleton {
  pointer-events: none;
}

.hero-card__portrait-area--skeleton {
  background: var(--theme-shimmer-from);
  animation: hero-shimmer 1.4s ease-in-out infinite;
}

.hero-card__skeleton-line {
  height: 16px;
  border-radius: 8px;
  background: var(--theme-shimmer-to);
  animation: hero-shimmer 1.4s ease-in-out infinite;
  width: 100%;
}

.hero-card__skeleton-line--title {
  height: 24px;
  width: 75%;
}

.hero-card__skeleton-line--short {
  width: 40%;
}

@keyframes hero-shimmer {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

/* Mobile: stack portrait above content */
@media (max-width: 600px) {
  .hero-card {
    flex-direction: column;
  }

  .hero-card__portrait-area {
    width: 100%;
    height: 180px;
  }

  .hero-card__portrait-fade {
    background: linear-gradient(to bottom, transparent 40%, var(--theme-surface-solid) 100%);
  }

  .hero-card__content {
    padding: 16px 20px 20px;
  }
}
</style>
